import React, { useCallback, useRef, useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import ReactFlow, {
  Background, Controls, MiniMap, Panel,
  useNodesState, useEdgesState, addEdge,
  Connection, Node, Edge, NodeTypes,
  BackgroundVariant,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { v4 as uuidv4 } from 'uuid';
import { clsx } from 'clsx';
import {
  Save, Play, Settings2, Layers, ArrowLeft,
  Undo2, Redo2, ZoomIn, ZoomOut, Maximize, Bot,
  Variable, X, CheckCircle2,
} from 'lucide-react';
import { useWorkflowStore } from '../store/workflow.store';
import { NodePalette } from '../components/workflow-builder/NodePalette';
import { NodeProperties } from '../components/workflow-builder/NodeProperties';
import { OrbayxNode } from '../components/workflow-builder/nodes/BaseNode';
import api from '../api/client';
import { WorkflowVariable } from '../types';

const NODE_TYPES: NodeTypes = {
  trigger_manual: OrbayxNode,
  trigger_schedule: OrbayxNode,
  trigger_event: OrbayxNode,
  trigger_api: OrbayxNode,
  trigger_webhook: OrbayxNode,
  action_ad: OrbayxNode,
  action_vm: OrbayxNode,
  action_storage: OrbayxNode,
  action_security: OrbayxNode,
  action_notification: OrbayxNode,
  action_itsm: OrbayxNode,
  control_condition: OrbayxNode,
  control_loop: OrbayxNode,
  control_parallel: OrbayxNode,
  control_wait: OrbayxNode,
  control_approval: OrbayxNode,
  integration_servicenow: OrbayxNode,
  integration_slack: OrbayxNode,
  integration_teams: OrbayxNode,
  integration_email: OrbayxNode,
  ai_analyze: OrbayxNode,
  ai_decide: OrbayxNode,
  ai_generate: OrbayxNode,
};

export function WorkflowBuilder() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const blueprintId = searchParams.get('blueprint');
  const workflowId = searchParams.get('id');
  const qc = useQueryClient();

  const reactFlowWrapper = useRef<HTMLDivElement>(null);
  const [reactFlowInstance, setReactFlowInstance] = useState<any>(null);

  const {
    workflowName, workflowDescription, workflowStatus, workflowId: storeWorkflowId,
    variables, isDirty,
    setWorkflowMeta, loadFromDefinition, markClean, reset,
    addVariable, removeVariable,
  } = useWorkflowStore();

  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [selectedNode, setSelectedNode] = useState<Node | null>(null);
  const [showVariables, setShowVariables] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [newVar, setNewVar] = useState({ name: '', type: 'string', defaultValue: '' });

  // Load blueprint if specified
  const { data: blueprintData } = useQuery({
    queryKey: ['blueprint', blueprintId],
    queryFn: async () => {
      const res = await api.get(`/blueprints/${blueprintId}`);
      return res.data.data;
    },
    enabled: !!blueprintId,
  });

  // Load existing workflow if specified
  const { data: workflowData } = useQuery({
    queryKey: ['workflow', workflowId],
    queryFn: async () => {
      const res = await api.get(`/workflows/${workflowId}`);
      return res.data.data;
    },
    enabled: !!workflowId,
  });

  // Load blueprint or workflow definition into canvas
  useEffect(() => {
    if (blueprintData) {
      setWorkflowMeta({ name: blueprintData.name, description: blueprintData.description });
      const { nodes: defNodes, edges: defEdges, variables: defVars } = blueprintData.definition;
      const rfNodes = defNodes.map((n: any) => ({
        id: n.id, type: n.type,
        position: n.position,
        data: { ...n.data, label: n.label, nodeType: n.type },
      }));
      const rfEdges = defEdges.map((e: any) => ({
        id: e.id, source: e.source, target: e.target,
        label: e.label,
        animated: e.type === 'conditional',
        style: { stroke: '#6366f1', strokeWidth: 2 },
        type: 'smoothstep',
      }));
      setNodes(rfNodes);
      setEdges(rfEdges);
    }
  }, [blueprintData]);

  useEffect(() => {
    if (workflowData) {
      setWorkflowMeta({ id: workflowData.id, name: workflowData.name, description: workflowData.description, status: workflowData.status });
      const { nodes: defNodes, edges: defEdges, variables: defVars } = workflowData.definition;
      const rfNodes = defNodes.map((n: any) => ({
        id: n.id, type: n.type,
        position: n.position,
        data: { ...n.data, label: n.label, nodeType: n.type },
      }));
      const rfEdges = defEdges.map((e: any) => ({
        id: e.id, source: e.source, target: e.target,
        label: e.label, animated: e.type === 'conditional',
        style: { stroke: '#6366f1', strokeWidth: 2 }, type: 'smoothstep',
      }));
      setNodes(rfNodes);
      setEdges(rfEdges);
    }
  }, [workflowData]);

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: async (status: string = 'draft') => {
      const definition = {
        nodes: nodes.map((n) => ({
          id: n.id,
          type: n.type,
          label: n.data.label || 'Unnamed',
          position: n.position,
          data: n.data,
        })),
        edges: edges.map((e) => ({
          id: e.id,
          source: e.source,
          target: e.target,
          sourceHandle: e.sourceHandle,
          targetHandle: e.targetHandle,
          label: e.label,
          condition: (e.data as any)?.condition,
          type: e.animated ? 'conditional' : 'default',
        })),
        variables,
      };

      if (storeWorkflowId) {
        return api.put(`/workflows/${storeWorkflowId}`, { name: workflowName, description: workflowDescription, definition, status });
      } else {
        const res = await api.post('/workflows', {
          name: workflowName, description: workflowDescription, definition,
          category: 'custom', status,
          trigger: { type: 'manual' },
          fromBlueprintId: blueprintId || undefined,
        });
        setWorkflowMeta({ id: res.data.data.id });
        return res;
      }
    },
    onSuccess: () => {
      markClean();
      qc.invalidateQueries({ queryKey: ['workflows'] });
      toast.success('Workflow saved successfully');
    },
    onError: () => toast.error('Failed to save workflow'),
  });

  // Execute mutation
  const executeMutation = useMutation({
    mutationFn: async () => {
      if (!storeWorkflowId) throw new Error('Save workflow first');
      if (workflowStatus !== 'active') {
        await api.put(`/workflows/${storeWorkflowId}`, { status: 'active' });
      }
      const res = await api.post(`/workflows/${storeWorkflowId}/execute`, { trigger: 'manual' });
      return res.data.data;
    },
    onSuccess: (data) => {
      toast.success(`Execution started: ${data.executionId}`);
      navigate(`/activity?execution=${data.executionId}`);
    },
    onError: (err: any) => toast.error(err.response?.data?.message || 'Execution failed'),
  });

  const onConnect = useCallback(
    (params: Connection) => setEdges((eds) => addEdge({
      ...params,
      animated: false,
      style: { stroke: '#6366f1', strokeWidth: 2 },
      type: 'smoothstep',
    }, eds)),
    [setEdges],
  );

  const onDragStart = (event: React.DragEvent, nodeType: string, label: string) => {
    event.dataTransfer.setData('application/reactflow/type', nodeType);
    event.dataTransfer.setData('application/reactflow/label', label);
    event.dataTransfer.effectAllowed = 'move';
  };

  const onDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();
      if (!reactFlowWrapper.current || !reactFlowInstance) return;

      const type = event.dataTransfer.getData('application/reactflow/type');
      const label = event.dataTransfer.getData('application/reactflow/label');
      if (!type) return;

      const bounds = reactFlowWrapper.current.getBoundingClientRect();
      const position = reactFlowInstance.screenToFlowPosition({
        x: event.clientX - bounds.left,
        y: event.clientY - bounds.top,
      });

      const newNode: Node = {
        id: uuidv4(),
        type,
        position,
        data: { label, nodeType: type },
      };

      setNodes((nds) => nds.concat(newNode));
    },
    [reactFlowInstance, setNodes],
  );

  const onDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const onNodeClick = useCallback((_: React.MouseEvent, node: Node) => {
    setSelectedNode(node);
  }, []);

  const onPaneClick = useCallback(() => {
    setSelectedNode(null);
  }, []);

  return (
    <div className="flex flex-col h-full">
      {/* Toolbar */}
      <div className="flex items-center gap-2 px-4 py-2.5 bg-white border-b border-gray-200">
        <button
          onClick={() => navigate('/workflows')}
          className="p-1.5 hover:bg-gray-50 rounded-lg text-gray-400 hover:text-gray-900 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
        </button>

        <div className="w-px h-4 bg-gray-100" />

        {/* Workflow name */}
        <input
          type="text"
          value={workflowName}
          onChange={(e) => setWorkflowMeta({ name: e.target.value })}
          className="bg-transparent border-none text-sm font-semibold text-white focus:outline-none hover:bg-gray-50 px-2 py-1 rounded-lg transition-colors min-w-0 max-w-[200px]"
          placeholder="Workflow name..."
        />

        {isDirty && <span className="text-[10px] text-yellow-400">Unsaved changes</span>}

        <div className="flex-1" />

        {/* Action buttons */}
        <button
          onClick={() => setShowVariables(!showVariables)}
          className={clsx(
            'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all',
            showVariables ? 'bg-orbayx-600/30 text-orbayx-700 border border-orbayx-500/30' : 'bg-gray-50 text-gray-500 hover:text-gray-900 hover:bg-gray-100',
          )}
        >
          <Variable className="w-3.5 h-3.5" />
          Variables ({variables.length})
        </button>

        <button
          className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-50 hover:bg-gray-100 rounded-lg text-xs text-gray-500 hover:text-gray-900 transition-all"
          onClick={() => toast('AI workflow generation coming soon', { icon: '🤖' })}
        >
          <Bot className="w-3.5 h-3.5" />
          AI Generate
        </button>

        <button
          onClick={() => saveMutation.mutate('draft')}
          disabled={saveMutation.isPending}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-lg text-xs text-gray-600 hover:text-gray-900 font-medium transition-all disabled:opacity-50"
        >
          <Save className="w-3.5 h-3.5" />
          {saveMutation.isPending ? 'Saving...' : 'Save'}
        </button>

        <button
          onClick={() => executeMutation.mutate()}
          disabled={executeMutation.isPending || !storeWorkflowId}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-orbayx-600 hover:bg-orbayx-500 rounded-lg text-xs text-white font-medium transition-all shadow-glow disabled:opacity-50"
        >
          <Play className="w-3.5 h-3.5" />
          {executeMutation.isPending ? 'Starting...' : 'Run'}
        </button>
      </div>

      {/* Main Area */}
      <div className="flex-1 flex overflow-hidden">
        {/* Node Palette */}
        <NodePalette onDragStart={onDragStart} />

        {/* Canvas */}
        <div ref={reactFlowWrapper} className="flex-1 relative" onDrop={onDrop} onDragOver={onDragOver}>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onInit={setReactFlowInstance}
            onNodeClick={onNodeClick}
            onPaneClick={onPaneClick}
            nodeTypes={NODE_TYPES}
            fitView
            fitViewOptions={{ padding: 0.2 }}
            minZoom={0.1}
            maxZoom={2}
            deleteKeyCode="Delete"
            className="!bg-[#f0f2f5]"
          >
            <Background
              variant={BackgroundVariant.Dots}
              gap={20}
              size={1}
              color="rgba(255,255,255,0.05)"
            />
            <Controls
              className="!bg-white !border-gray-200 !rounded-xl"
              showInteractive={false}
            />
            <MiniMap
              className="!bg-white !border-gray-200 !rounded-xl"
              nodeColor={(n) => {
                const type = n.type || '';
                if (type.startsWith('trigger_')) return '#10b981';
                if (type.startsWith('ai_')) return '#8b5cf6';
                if (type.startsWith('control_')) return '#f59e0b';
                return '#6366f1';
              }}
              maskColor="rgba(0,0,0,0.3)"
            />

            {/* Empty state */}
            {nodes.length === 0 && (
              <Panel position="top-center">
                <div className="mt-20 text-center pointer-events-none">
                  <Layers className="w-12 h-12 text-white/10 mx-auto mb-3" />
                  <div className="text-gray-400 text-sm">Drag nodes from the palette to get started</div>
                  <div className="text-gray-300 text-xs mt-1">or <span className="text-orbayx-500">deploy a Blueprint</span> as a starting point</div>
                </div>
              </Panel>
            )}
          </ReactFlow>
        </div>

        {/* Node Properties Panel */}
        {selectedNode && (
          <NodeProperties
            node={selectedNode}
            onClose={() => setSelectedNode(null)}
          />
        )}

        {/* Variables Panel */}
        {showVariables && !selectedNode && (
          <div className="w-72 bg-white border-l border-gray-200 flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <div className="text-sm font-semibold text-white">Workflow Variables</div>
              <button onClick={() => setShowVariables(false)} className="text-gray-400 hover:text-gray-900 p-1 hover:bg-gray-50 rounded-lg transition-colors">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {variables.map((v) => (
                <div key={v.name} className="flex items-center justify-between p-2.5 bg-gray-50 border border-gray-200 rounded-lg">
                  <div>
                    <div className="text-xs font-mono text-orbayx-600">{`{{${v.name}}}`}</div>
                    <div className="text-[10px] text-gray-400 mt-0.5">{v.type}{v.description ? ` · ${v.description}` : ''}</div>
                  </div>
                  <button onClick={() => removeVariable(v.name)} className="text-gray-300 hover:text-red-400 transition-colors">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
              {variables.length === 0 && (
                <div className="text-xs text-gray-400 italic">No variables defined. Variables can be referenced using {'{{variable_name}}'} syntax.</div>
              )}
            </div>
            <div className="p-4 border-t border-gray-200 space-y-2">
              <div className="text-xs text-gray-500 font-medium">Add Variable</div>
              <input
                type="text"
                value={newVar.name}
                onChange={(e) => setNewVar((v) => ({ ...v, name: e.target.value }))}
                placeholder="Variable name"
                className="w-full bg-gray-50 border border-gray-200 rounded-lg px-3 py-1.5 text-xs text-white focus:outline-none focus:border-orbayx-500 transition-all"
              />
              <select
                value={newVar.type}
                onChange={(e) => setNewVar((v) => ({ ...v, type: e.target.value }))}
                className="w-full bg-gray-50 border border-gray-200 rounded-lg px-3 py-1.5 text-xs text-white focus:outline-none focus:border-orbayx-500 transition-all"
              >
                <option value="string">string</option>
                <option value="number">number</option>
                <option value="boolean">boolean</option>
                <option value="array">array</option>
                <option value="object">object</option>
              </select>
              <button
                onClick={() => {
                  if (newVar.name) {
                    addVariable({ name: newVar.name, type: newVar.type as any, defaultValue: newVar.defaultValue });
                    setNewVar({ name: '', type: 'string', defaultValue: '' });
                  }
                }}
                className="w-full py-1.5 bg-orbayx-600 hover:bg-orbayx-500 rounded-lg text-xs text-white font-medium transition-all"
              >
                Add Variable
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
